import { useState, useEffect } from 'react';
import { api } from '../../../services/api';

export type StageDateEntry = {
  start: string;
  completion: string;
  extractedStageId?: string;
};

type ExtractedStageItem = {
  id: string;
  canonical_stage: string;
  stage_name: string;
  /** Canonical key from the API — same for every spelling of a stage. */
  stage_key?: string | null;
  start_date_noted: string | null;
  completion_date_noted: string | null;
  created_at: string;
};

type StageItem = {
  stage_name: string;
  construction_stage_key?: string | null;
  [key: string]: any;
};

type StagesData =
  | {
      stages: StageItem[];
    }
  | null
  | undefined;

export function useStageDateEditing(
  extractedStagesData: ExtractedStageItem[] | undefined,
  stagesData: StagesData
) {
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);
  const [stageDates, setStageDates] = useState<Record<string, StageDateEntry>>(
    {}
  );

  // Convert datetime string (ISO or space-separated) to YYYY-MM-DD for date inputs
  const toDateOnly = (dt: string | null | undefined): string => {
    if (!dt) return '';
    // Handle "2026-03-31T00:00:00" or "2026-03-31 00:00:00" — extract date part
    return dt.slice(0, 10);
  };

  // Pre-populate stage dates from most recent extracted observations per stage
  useEffect(() => {
    if (!extractedStagesData || !stagesData?.stages?.length) return;
    setStageDates((prev) => {
      if (Object.keys(prev).length > 0) return prev; // don't overwrite user edits
      const byStage: Record<string, ExtractedStageItem> = {};
      extractedStagesData.forEach((obs) => {
        const key = (obs.canonical_stage || obs.stage_name).toLowerCase();
        if (!byStage[key] || obs.created_at > byStage[key].created_at) {
          byStage[key] = obs;
        }
      });
      // Newest observation per canonical stage key: the API folds every
      // spelling ("BMP Installations" / "BMP Installation" / "Installation")
      // to one key, so a legacy date lands on Jim's stage whatever it is called.
      const byKey: Record<string, ExtractedStageItem> = {};
      extractedStagesData.forEach((obs) => {
        if (!obs.stage_key) return;
        if (
          !byKey[obs.stage_key] ||
          obs.created_at > byKey[obs.stage_key].created_at
        ) {
          byKey[obs.stage_key] = obs;
        }
      });
      const initial: Record<string, StageDateEntry> = {};
      const normalize = (s: string) => s.toLowerCase().replace(/\s+/g, '_');
      stagesData.stages.forEach((stage) => {
        const normalizedName = normalize(stage.stage_name);
        const keyed = stage.construction_stage_key
          ? byKey[stage.construction_stage_key]
          : undefined;
        const matchKey = keyed
          ? undefined
          : Object.keys(byStage).find(
              (k) =>
                k === stage.stage_name.toLowerCase() ||
                normalize(k) === normalizedName
            );
        const match = keyed ?? (matchKey ? byStage[matchKey] : undefined);
        initial[stage.stage_name] = {
          start: toDateOnly(match?.start_date_noted),
          completion: toDateOnly(match?.completion_date_noted),
          extractedStageId: match?.id,
        };
      });
      return initial;
    });
  }, [extractedStagesData, stagesData]);

  // Set a stage date and immediately save if there's an existing record to PATCH.
  // For stages without an existing DB record, the date is stored in React state and
  // a new ExtractedStageObservation is created at submission time (with the real inspection ID).
  const handleStageDateSetAndSave = async (
    stageName: string,
    field: 'start' | 'completion',
    value: string
  ) => {
    // Read extractedStageId from current state before the async setStageDates
    const existingId = stageDates[stageName]?.extractedStageId;
    setStageDates((prev) => ({
      ...prev,
      [stageName]: {
        start: prev[stageName]?.start || '',
        completion: prev[stageName]?.completion || '',
        extractedStageId: prev[stageName]?.extractedStageId,
        [field]: value,
      },
    }));
    if (existingId) {
      try {
        await api.patch(`/extracted-stages/${existingId}`, {
          [field === 'start' ? 'start_date_noted' : 'completion_date_noted']:
            value || null,
        });
      } catch (err) {
        console.error('Failed to save stage date:', err);
      }
    }
  };

  // Save on blur for manually-edited date inputs
  const handleStageDateBlur = async (
    stageName: string,
    field: 'start' | 'completion'
  ) => {
    const entry = stageDates[stageName];
    if (!entry?.extractedStageId) return;
    try {
      await api.patch(`/extracted-stages/${entry.extractedStageId}`, {
        [field === 'start' ? 'start_date_noted' : 'completion_date_noted']:
          (field === 'start' ? entry.start : entry.completion) || null,
      });
    } catch (err) {
      console.error('Failed to save stage date:', err);
    }
  };

  return {
    selectedStageId,
    setSelectedStageId,
    stageDates,
    setStageDates,
    handleStageDateSetAndSave,
    handleStageDateBlur,
  };
}
